import { useNavigate } from "react-router-dom";
import { AuthImage } from "@/components/AuthImage";
import { stageMeta, relativeDays } from "@/lib/plant";
import { Droplets, MapPin, Ruler } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export function PlantCard({ plant, onChanged }) {
  const navigate = useNavigate();
  const meta = stageMeta(plant.growth_stage);
  const StageIcon = meta.icon;

  const water = async (e) => {
    e.stopPropagation();
    try {
      await api.post(`/plants/${plant.id}/water`);
      toast.success(`${plant.name} watered 💧`);
      onChanged?.();
    } catch {
      toast.error("Could not update");
    }
  };

  return (
    <div
      onClick={() => navigate(`/sprouts/${plant.id}`)}
      className="group cursor-pointer bg-card rounded-2xl border border-border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden animate-rise"
      data-testid="plant-card-item"
    >
      <div className="relative h-44 overflow-hidden">
        <AuthImage path={plant.photo_path} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" fallbackClassName="w-full h-full" />
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.color}`}>
            <StageIcon className="h-3.5 w-3.5" />
            {plant.growth_stage}
          </span>
        </div>
        {plant.needs_water && (
          <div className="absolute top-3 right-3 bg-sky-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 shadow">
            <Droplets className="h-3.5 w-3.5" /> Thirsty
          </div>
        )}
      </div>
      <div className="p-4 space-y-3">
        <div>
          <h3 className="font-display font-semibold text-base leading-tight truncate">{plant.name}</h3>
          <p className="text-xs text-muted-foreground truncate">
            {[plant.species, plant.variety].filter(Boolean).join(" · ") || "Unspecified"}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {plant.location && (
            <span className="flex items-center gap-1 truncate">
              <MapPin className="h-3.5 w-3.5" /> {plant.location}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Droplets className="h-3.5 w-3.5" /> {plant.last_watered ? relativeDays(plant.last_watered) : "never"}
          </span>
        </div>
        <button
          onClick={water}
          className="w-full bg-sky-500 hover:bg-sky-600 text-white font-medium py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-1.5 active:scale-[0.98]"
          data-testid="water-now-button"
        >
          <Droplets className="h-4 w-4" /> Water now
        </button>
      </div>
    </div>
  );
}
